import { createFileRoute, Link } from '@tanstack/react-router';
import { buildCanonicalLink, buildMeta } from '@/lib/seo';
import { ssrForPublicOnly } from '@/lib/ssr-utils';

export const Route = createFileRoute('/terms-of-use/')({
  ssr: ssrForPublicOnly,
  head: () => ({
    meta: buildMeta({
      title: 'תנאי שימוש - Pricedrop',
      description: 'קראו את תנאי השימוש של שירות מעקב המחירים Pricedrop.',
      canonicalUrl: '/terms-of-use',
    }),
    links: [buildCanonicalLink('/terms-of-use')],
  }),
  component: TermsOfUsePage,
});

function TermsOfUsePage() {
  return (
    <div className="mx-auto max-w-4xl rounded-lg bg-card p-6 shadow-md">
      <h1 className="mb-6 text-3xl font-bold">תנאי שימוש</h1>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">מבוא</h2>
        <p>
          ברוכים הבאים ל-<strong>Pricedrop</strong> (להלן: &quot;השירות&quot;). תנאי שימוש אלה חלים
          על השימוש באתר Pricedrop.co.il, בתוסף הדפדפן ובכל השירותים הנלווים, כולל מעקב מחירים,
          התראות וביקורות משתמשים. השימוש בשירות מהווה הסכמה לתנאים אלה. אם אינכם מסכימים לתנאים,
          אנא הימנעו משימוש בשירות.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">הגדרות</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>
            <strong>&quot;השירות&quot;</strong> — אתר Pricedrop.co.il, תוסף הדפדפן, מערכת ההתראות
            וכלל הכלים הנלווים.
          </li>
          <li>
            <strong>&quot;משתמש&quot;</strong> — כל אדם הגולש באתר או משתמש בשירות, בין אם נרשם
            לחשבון ובין אם לא.
          </li>
          <li>
            <strong>&quot;תוכן משתמש&quot;</strong> — ביקורות, דירוגים, תמונות וכל מידע אחר שמשתמשים
            מעלים לשירות.
          </li>
          <li>
            <strong>&quot;מוצר&quot;</strong> — פריט המוצג באתר, כולל מידע על מחיר, מפרט וזמינות,
            כפי שנאסף ממקורות חיצוניים.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">אופי השירות</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>
            Pricedrop הוא שירות חינמי למעקב אחר מחירי מוצרים באתר KSP.co.il. השירות מציג היסטוריית
            מחירים, מאפשר הגדרת התראות על שינויי מחירים, ומציע תוסף דפדפן להצגת מידע על מחירים
            ישירות בדפי מוצרים.
          </li>
          <li>
            מידע המוצרים והמחירים נאסף ממקורות חיצוניים באופן אוטומטי. Pricedrop אינו מתחייב לדיוק
            מוחלט, לעדכניות או לזמינות רציפה של המידע.
          </li>
          <li>
            Pricedrop אינו חנות מקוונת ואינו מוכר מוצרים. השירות מספק מידע בלבד ואינו קשור מסחרית
            לאתר KSP או לכל קמעונאי אחר.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">הרשמה וחשבון משתמש</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>
            גלישה באתר וצפייה במידע על מוצרים אינן דורשות הרשמה. הרשמה נדרשת לשימוש בשירותים כגון
            מעקב מחירים, התראות וכתיבת ביקורות.
          </li>
          <li>ניתן להירשם באמצעות כתובת דוא&quot;ל וסיסמה, או דרך חשבון Google.</li>
          <li>המשתמש אחראי לשמור על פרטי ההתחברות שלו ולדווח מיד על כל שימוש לא מורשה בחשבונו.</li>
          <li>Pricedrop שומר לעצמו את הזכות להשעות או למחוק חשבונות שמפרים תנאים אלה.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">תוסף הדפדפן</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>
            תוסף Pricedrop לדפדפן מציג גרף היסטוריית מחירים ישירות בדפי מוצרים באתר KSP.co.il.
          </li>
          <li>
            התוסף פועל אך ורק באתר KSP ואינו אוסף מידע אישי. התוסף קורא את כתובת הדף בלבד כדי לזהות
            את המוצר המוצג.
          </li>
          <li>התוסף זמין לדפדפנים מבוססי Chromium (כגון Chrome, Edge ו-Brave) ול-Firefox.</li>
          <li>
            Pricedrop אינו אחראי לתקלות שנגרמות עקב שינויים באתר KSP או עדכוני דפדפן שעלולים להשפיע
            על פעולת התוסף.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">ביקורות ותוכן משתמשים</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>משתמשים רשומים יכולים לפרסם ביקורות על מוצרים, כולל דירוג, טקסט ותמונות.</li>
          <li>ביקורות מוצגות באופן פומבי ומשויכות לשם המשתמש ותמונת הפרופיל שלו.</li>
          <li>
            המשתמש מתחייב שתוכן הביקורות שלו אמיתי, אינו פוגעני, אינו מפר זכויות יוצרים ואינו מכיל
            תוכן בלתי חוקי.
          </li>
          <li>
            Pricedrop שומר לעצמו את הזכות להסיר או לערוך ביקורות שמפרות תנאים אלה, ללא הודעה מוקדמת.
          </li>
          <li>
            הבעלות על תוכן הביקורות נשארת בידי המשתמש, אך בפרסום ביקורת המשתמש מעניק ל-Pricedrop
            רישיון להציג את התוכן במסגרת השירות.
          </li>
          <li>תמונות שמועלות לביקורות מאוחסנות בשרתים מאובטחים. ניתן למחוק תמונות בכל עת.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">התראות ומעקב מחירים</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>
            משתמשים רשומים יכולים להגדיר התראות על שינויי מחירים, ירידות מחיר, חזרה למלאי ועוד.
          </li>
          <li>
            התראות נשלחות בערוצים שהמשתמש בחר: דוא&quot;ל, Telegram או WhatsApp. חיבור ל-Telegram
            ו-WhatsApp מתבצע באופן יזום על ידי המשתמש.
          </li>
          <li>
            Pricedrop אינו מתחייב למשלוח התראות בזמן אמת ואינו אחראי לעיכובים או לתקלות במשלוח
            הודעות.
          </li>
          <li>המשתמש יכול לבטל התראות, לנתק ערוצי תקשורת ולמחוק הגדרות מעקב בכל עת.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">שימוש הוגן</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>השירות פתוח לשימוש אישי בלבד.</li>
          <li>אין לעשות שימוש בשירות למטרות לא חוקיות, פוגעניות או כאלה הפוגעות בזכויות אחרים.</li>
          <li>
            אין לבצע גריפה אוטומטית (scraping), לשלוח בקשות אוטומטיות בכמות גדולה או להעמיס על שרתי
            השירות.
          </li>
          <li>אין להתחזות למשתמש אחר או לפעול בשם גורם אחר ללא הרשאה.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">קניין רוחני</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>
            העיצוב, הקוד, הלוגו והתוכן המקורי של Pricedrop שייכים ל-Pricedrop ומוגנים בזכויות
            יוצרים.
          </li>
          <li>מידע על מוצרים ומחירים שייך למקורות המקוריים שלו ומוצג בשירות לצורכי מידע בלבד.</li>
          <li>תוכן שנוצר על ידי משתמשים (ביקורות, תמונות) נשאר בבעלות המשתמש.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">הגבלת אחריות</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>
            השירות מסופק כפי שהוא (&quot;As-Is&quot;) ללא כל התחייבות לדיוק, זמינות או התאמה לצורך
            מסוים.
          </li>
          <li>
            Pricedrop אינו אחראי לנזק ישיר או עקיף שנגרם כתוצאה משימוש בשירות או הסתמכות על המידע
            המוצג בו.
          </li>
          <li>Pricedrop אינו אחראי להחלטות רכישה של משתמשים ואינו מספק המלצות קנייה.</li>
          <li>Pricedrop אינו קשור מסחרית ל-KSP.co.il או לכל קמעונאי אחר המוזכר בשירות.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">שינויים בתנאים</h2>
        <p>
          Pricedrop רשאי לעדכן תנאי שימוש אלה מעת לעת. המשך השימוש בשירות לאחר עדכון התנאים מהווה
          הסכמה לתנאים המעודכנים. אנו ממליצים לעיין בדף זה מדי פעם.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">פרטיות</h2>
        <p>
          המידע על אופן איסוף, שימוש ושמירת המידע האישי שלכם מפורט ב
          <Link to="/privacy-policy" className="text-primary hover:underline">
            מדיניות הפרטיות
          </Link>{' '}
          שלנו, המהווה חלק בלתי נפרד מתנאי שימוש אלה.
        </p>
      </section>

      <section>
        <h2 className="mb-4 text-2xl font-semibold">יצירת קשר</h2>
        <p>
          לשאלות או הבהרות בנוגע לתנאי שימוש אלה, ניתן לפנות אלינו דרך{' '}
          <Link to="/contact" className="text-primary hover:underline">
            דף יצירת הקשר
          </Link>
          .
        </p>
      </section>
    </div>
  );
}
